import { ReactNode } from "react";
import { Stack, Box, useTheme, Button } from "@mui/material";
import { useResponsiveBreakpoints } from "src/utils/useResponsiveBreakpoints";
import { FoodsIcon, useFormBaseContext } from "@foodpilot/foods";
import { useApplicationParameters } from "src/api/client-api/appParams";
import { Loading } from "src/components/utils/Loading";

type QuestionnaireLayoutProps = {
    children: ReactNode;
    scoreSection?: ReactNode;
    hasScores?: boolean;
    isLoading?: boolean;
};

export const QuestionnaireLayout = ({
    children,
    scoreSection,
    hasScores = false,
    isLoading = false,
}: QuestionnaireLayoutProps) => {
    const { isMobile, isTablet } = useResponsiveBreakpoints();
    const theme = useTheme();
    const { applicationParameters } = useApplicationParameters();
    const freshdeskValue = applicationParameters?.freshdeskWidgetId || "";
    const isFreshdeskActivated = freshdeskValue !== "";

    const shouldShrink = isMobile || isTablet;

    if (isLoading) {
        return (
            <Stack role="status" aria-label="Loading questionnaire" width="100%" alignItems="center">
                <Loading aria-label="Loading questionnaire" />
            </Stack>
        );
    }

    return (
        <Stack
            display={shouldShrink ? "block" : "flex"}
            spacing={shouldShrink ? theme.spacing(2) : theme.spacing(0)}
            gap={shouldShrink ? theme.spacing(0) : theme.spacing(2)}
            direction={shouldShrink ? "column" : "row"}
        >
            <Box flex={shouldShrink ? 1 : 3} display={"flex"} flexDirection={"column"} justifyContent={"space-between"}>
                {children}
                <Box
                    sx={{
                        zIndex: 1000,

                        // This will make up the buttons are a little bit higher
                        // on mobile view, if a problematic feature is activated that
                        // would block the view of the button
                        minHeight: shouldShrink && isFreshdeskActivated ? "150px" : 0,

                        padding: theme.spacing(2, 0),
                        background: theme.custom.grey[300],
                        position: "sticky",
                        bottom: 0,
                    }}
                >
                    <QuestionnaireButtons />
                </Box>
            </Box>
            {hasScores && (
                <Box
                    sx={{
                        maxWidth: shouldShrink ? "100%" : "450px",
                        flex: shouldShrink ? 1 : 2,
                    }}
                >
                    {scoreSection}
                </Box>
            )}
        </Stack>
    );
};

const QuestionnaireButtons = () => {
    const {
        onPageChange,
        selectedPageIndex,
        textOptions,
        previousLock,
        isLoading,
        setFormSaved,
        setPage,
        maxPage,
        forwardLock,
        onValidate,
    } = useFormBaseContext();
    const { isMobile, isTablet } = useResponsiveBreakpoints();
    const theme = useTheme();

    const isLastPage = selectedPageIndex === maxPage - 1;
    const nextPageText = isLastPage && textOptions.finalPage ? textOptions.finalPage : textOptions.nextPage;

    const shouldShrink = isMobile || isTablet;

    if (isLoading) return null;

    return (
        <Stack marginTop={1} flexDirection={"row"} justifyContent={"space-between"}>
            {textOptions.previousPage && (
                <Button
                    sx={{ minWidth: "90px" }}
                    variant="text"
                    disabled={selectedPageIndex === 0 || previousLock.isLocked || isLoading}
                    startIcon={
                        selectedPageIndex === 0 || previousLock.isLocked || isLoading ?
                            null
                        :   <FoodsIcon icon="arrowLeft" size={2} />
                    }
                    onClick={() => {
                        setFormSaved(false);
                        if (selectedPageIndex === 0) {
                            onPageChange?.(selectedPageIndex, selectedPageIndex);
                            return;
                        }

                        onPageChange?.(selectedPageIndex - 1, selectedPageIndex);
                        return setPage(selectedPageIndex - 1);
                    }}
                >
                    {textOptions.previousPage}
                </Button>
            )}
            <Button
                variant="primary"
                disabled={forwardLock.isLocked || isLoading}
                endIcon={<FoodsIcon icon="arrowTrajectory" size={2} />}
                onClick={() => {
                    if (selectedPageIndex === maxPage - 1) {
                        setFormSaved(true);
                        onValidate?.();
                        return;
                    }
                    setFormSaved(false);

                    onPageChange?.(selectedPageIndex + 1, selectedPageIndex);
                    return setPage(selectedPageIndex + 1);
                }}
                sx={
                    isMobile ?
                        {
                            minWidth: "80px",
                            fontSize: "0.875rem",
                            padding: "8px 12px",
                        }
                    :   {}
                }
            >
                {nextPageText}
            </Button>
        </Stack>
    );
};
